// glossary.js — plain-language names and explanations for training terms
// (pure, no React). Simple mode shows the plain name; Advanced shows the
// lifter's name. Every remaining term gets a tap-to-explain (?) via
// explain.jsx, which reads its text from here.
//
//   GLOSSARY[id]           → { plain, pro, short, body }
//   termLabel(id, simple)  → the name to show
//   plainEffort(rpe)       → "2 reps left in the tank"
//   effortLabel(rpe, scale, simple) → plain text in simple mode, "@8" / "2 RIR" otherwise
//   volumeBand(sets, lm, simple)    → { key, label } for weekly sets against the landmarks
//   simpleDefault(state)   → is simple mode on (pref, else from the profile's experience)

export const GLOSSARY = {
  'working-max': {
    plain: 'Working max', pro: 'Training max',
    short: 'A slightly-below-max number the program uses to set your weights.',
    body: 'About 90% of the most you could lift once. Programs that work in percentages use it to pick each day\'s weights, so you train heavy without grinding out maximal lifts. It goes up as you get stronger.',
  },
  effort: {
    plain: 'Effort', pro: 'RPE',
    short: 'How hard a set felt, by how many more reps you could have done.',
    body: 'RPE (rate of perceived exertion) scores a set out of 10 by the reps you had left: 10 means nothing left, 9 means 1 more rep, 8 means 2 more, 7 means 3 more. Most working sets should end with 1–3 reps left.',
  },
  rir: {
    plain: 'Reps left in the tank', pro: 'RIR',
    short: 'How many more good reps you could have done.',
    body: 'Reps in reserve: stop a set with this many clean reps still possible. "2 RIR" means you could have done 2 more. It is the same scale as RPE, counted the other way (RIR 2 = RPE 8).',
  },
  e1rm: {
    plain: 'Estimated max', pro: 'e1RM',
    short: 'The most you could likely lift once, worked out from a normal set.',
    body: 'Calculated from the weight and reps of your best recent set (for example, 100 kg for 8 reps ≈ 125 kg for one), so you can track strength without ever testing a true max.',
  },
  'one-rm': {
    plain: 'One-rep max', pro: '1RM',
    short: 'The most weight you can lift for a single rep.',
    body: 'Your one-rep max. Lift Log estimates it from your normal sets — you never need to test it.',
  },
  volume: {
    plain: 'Weekly sets', pro: 'Volume',
    short: 'Hard sets per muscle in a week — the main driver of muscle growth.',
    body: 'A set counts when it is taken close to failure (about 1–3 reps left). Most people grow well on roughly 10–20 hard sets per muscle per week, built up gradually.',
  },
  mev: {
    plain: 'Enough', pro: 'MEV',
    short: 'The fewest weekly sets that still build muscle.',
    body: 'Minimum effective volume: below this many hard sets a week, a muscle mostly holds steady instead of growing. Simple mode calls the range from here up "enough".',
  },
  mav: {
    plain: 'A lot', pro: 'MAV',
    short: 'Where weekly sets stop adding much extra growth.',
    body: 'Maximum adaptive volume: the top of the productive range. Above it you are doing a lot — fine for a few weeks, but recovery starts to suffer.',
  },
  mrv: {
    plain: 'Too much', pro: 'MRV',
    short: 'More weekly sets than you can recover from.',
    body: 'Maximum recoverable volume: past this, extra sets cause fatigue faster than they build muscle. A lighter week (deload) brings you back down.',
  },
  deload: {
    plain: 'Lighter week', pro: 'Deload',
    short: 'A planned easy week so your body can catch up.',
    body: 'Fewer sets and lighter weights for a week. Fatigue drops, joints get a break, and you usually come back stronger. Most programs build one in every 4–6 weeks.',
  },
  'plus-set': {
    plain: 'As many reps as you can', pro: 'AMRAP',
    short: 'The last set marked "+": do as many good reps as you can.',
    body: 'Stop when the next rep would break form, not at true failure. The number of reps tells the program whether to raise your working max.',
  },
  tier: {
    plain: 'Heavy / medium / light', pro: 'Tier (T1/T2/T3)',
    short: 'Tier 1 is the heavy main lift, tier 2 the medium work, tier 3 the light pump work.',
    body: 'Tiered programs give each exercise a job. T1: heavy, low reps. T2: moderate weight, more reps. T3: light, high reps for muscle. Each tier progresses by its own rules.',
  },
  meso: {
    plain: 'Training block', pro: 'Mesocycle',
    short: 'A few weeks of building work, then a lighter week.',
    body: 'A block of usually 4–6 weeks where sets or effort build week by week, ending with a deload. Then the next block starts a little higher.',
  },
  'double-progression': {
    plain: 'Reps, then weight', pro: 'Double progression',
    short: 'Add reps until you hit the top of the range, then add weight.',
    body: 'Each exercise has a rep range like 8–12. Add a rep or two each time; once every set reaches 12, add a little weight and start again near 8.',
  },
  'top-set': {
    plain: 'Heaviest set', pro: 'Top set',
    short: 'The one heavy set of the day; lighter sets follow.',
    body: 'Work up to one hard set, then do a few lighter "back-off" sets for practice and volume. The app sets the back-off weights from how the top set went.',
  },
  readiness: {
    plain: 'Readiness', pro: 'Readiness',
    short: 'A rough read of how recovered you are, from your recent training.',
    body: 'Based on how much and how hard you trained in the last week or two versus your usual. It is a nudge, not a diagnosis — how you feel today counts more.',
  },
  plateau: {
    plain: 'Stalled', pro: 'Plateau',
    short: 'Your estimated max hasn\'t gone up for several sessions.',
    body: 'Normal and fixable: change the rep range, swap a variation, add a set, or take a lighter week. The coach can suggest which.',
  },
  pr: {
    plain: 'Personal best', pro: 'PR',
    short: 'Your best ever on a lift — heaviest weight, most reps or best estimated max.',
    body: 'A personal record. Lift Log tracks best weight, best reps at a weight and best estimated max for every exercise.',
  },
};

export function termLabel(id, simple) {
  const g = GLOSSARY[id];
  if (!g) return id;
  return simple ? g.plain : g.pro;
}

const trim = (n) => (Math.abs(n - Math.round(n)) < 0.05 ? String(Math.round(n)) : n.toFixed(1));

// "2 reps left in the tank" from an RPE (storage is always RPE).
export function plainEffort(rpe) {
  if (rpe == null || !(rpe > 0)) return '';
  const left = Math.max(0, 10 - rpe);
  if (left <= 0.25) return 'All-out — nothing left';
  if (left >= 4) return '4+ reps left in the tank';
  if (Math.abs(left - Math.round(left)) < 0.05) return `${trim(left)} rep${Math.round(left) === 1 ? '' : 's'} left in the tank`;
  return `${Math.floor(left)}–${Math.ceil(left)} reps left in the tank`;
}
// Short form for tight cells: "2 left".
export function plainEffortShort(rpe) {
  if (rpe == null || !(rpe > 0)) return '';
  const left = Math.max(0, 10 - rpe);
  if (left <= 0.25) return 'All-out';
  if (left >= 4) return '4+ left';
  return Math.abs(left - Math.round(left)) < 0.05 ? `${trim(left)} left` : `${Math.floor(left)}–${Math.ceil(left)} left`;
}

// Effort text for a prescription or a logged set.
export function effortLabel(rpe, scale = 'rpe', simple = false) {
  if (rpe == null || !(rpe > 0)) return '';
  if (simple) return plainEffort(rpe);
  if (scale === 'rir') { const r = Math.max(0, 10 - rpe); return `${r >= 4 ? '4+' : trim(r)} RIR`; }
  return `@${trim(rpe)}`;
}

// Weekly sets against volume landmarks { mev, mav, mrv }.
export function volumeBand(v, lm, simple = false) {
  if (!lm) return { key: 'ok', label: simple ? 'Enough' : 'In range' };
  if (lm.mev > 0 && v < lm.mev) return { key: 'low', label: simple ? 'Not enough yet' : 'Below MEV' };
  if (v > lm.mrv) return { key: 'over', label: simple ? 'Too much' : 'Over MRV' };
  if (v > lm.mav) return { key: 'high', label: simple ? 'A lot' : 'High' };
  return { key: 'ok', label: simple ? 'Enough' : 'In range' };
}

// Simple mode: the user's choice when they made one; otherwise on for people
// who told onboarding they are new or a beginner, off for everyone else.
export function simpleDefault(state) {
  const pref = state?.prefs?.simpleMode;
  if (typeof pref === 'boolean') return pref;
  const exp = state?.profile?.experience;
  return exp === 'new' || exp === 'beginner';
}
